import { DndContext } from '@dnd-kit/core';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import PersonAddOutlined from '@mui/icons-material/PersonAddOutlined';
import SendOutlined from '@mui/icons-material/SendOutlined';
import SmsOutlined from '@mui/icons-material/SmsOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  OrderState,
  UserBulkAssignJobsChannel_Read,
  UserBulkCopyAssignmentsChannel_Read,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import { BulkActionsBarButton } from '~components/BulkActionsBar/BulkActionsBar';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import OrdersStatusFilter from '~components/Filters/OrdersStatusFilter';
import SalespersonViewSelector from '~components/Filters/SalespersonViewSelector';
import BulkEditJobDialog from '~components/Job/BulkEditJobDialog';
import SendTextDialog from '~components/Job/SendTextDialog';
import {
  LoadingSpinner,
  SmallButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { OrdersAssignedTargetEntity } from '~constants/dispatch';
import { FetchReason } from '~constants/enums';
import { OrdersAssignedStorageKeys } from '~constants/ordersAssigned';
import { Order } from '~hooks/useOrders/models';
import { useOrders } from '~hooks/useOrders/useOrders';
import useOrdersAssigned from '~hooks/useOrders/useOrdersAssigned';
import useOrdersDispatch from '~hooks/useOrders/useOrdersDispatch';
import { BulkAssignJobsDialog } from '~pages/Dispatch/components/BulkAssignJobsDialog';
import { useStores } from '~store';
import {
  Filters as OrdersAssignedStoreFilters,
  ORDER_ASSIGNED_CARD_DEFAULT_EXPANDED_STATE,
} from '~store/OrdersAssignedStore';
import { alert, AlertTypes } from '~types/AlertTypes';
import { usePrevious } from '~utils/hooks/usePrevious';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import BulkAssignMultipleJobsDialog from '../Dispatch/components/BulkAssignMultipleJobsDialog';
import CloneOrderDialog from '../Dispatch/components/CloneOrderDialog';
import {
  MODAL_DIALOG_ANIMATION_DURATION,
  ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE,
} from '../Dispatch/components/constants';
import CopyVendorAssignmentsDialog from '../Dispatch/components/CopyVendorAssignmentsDialog';
import CreateOrderButton from '../Dispatch/components/CreateOrderButton';
import { HEADER_HEIGHT } from '../Dispatch/components/drivers/constants';
import OrderDispatchCard from '../Dispatch/components/OrderDispatchCard';
import OrderForm from '../Dispatch/components/OrderForm';
import OrdersAssignedDatesFilter from '../Dispatch/components/OrdersAssignedDatesFilter';
import { ExpandedStatesMenu } from '../Dispatch/components/OrdersDispatchFiltersBar';
import OrdersViewBulkActionsBar, {
  useOrdersViewBulkActionsBar,
} from '../Dispatch/components/OrdersViewBulkActionsBar';
import RefreshOrdersListButton from '../Dispatch/components/RefreshOrdersListButton';
import RejectOrderDialog from '../Dispatch/components/RejectOrderDialog';
import { AssignedOrdersProjectsQueryContextProvider } from './AssignedOrdersProjectsQueryContext';
import ProjectsList from './ProjectsList';

export enum AssignedOrdersViewTab {
  FOREMAN = 'foreman',
  DRIVE = 'drive',
  COLLABORATOR = 'collaborator',
  SALES = 'sales',
}

enum OrderActionDialog {
  BULK_ASSIGN_MULTIPLE = 'bulk_assign_multiple',
  CANCEL_ORDER = 'cancel_order',
  CLONE_ORDER = 'clone_order',
  TEXT_ALL_DRIVERS = 'text_all_drivers',
  REJECT_ORDER = 'reject_order',
}

type OrderActionDialogState = {
  dialog?: OrderActionDialog;
  isOpen: boolean;
  order?: Order;
};

type OrderFormState = {
  drawer: {
    isMinimized: boolean;
    isOpen: boolean;
    order?: Order;
  };
  confirmationDialog: {
    isOpen: boolean;
    order?: Order;
  };
};

const ordersFetchInitialState = {
  initialFetchComplete: false,
  reason: FetchReason.INITIAL_FETCH,
  shouldFetch: false,
};

const ORDER_DISPATCH_V3_GRID_ROW_HEIGHT_IN_PX = 47;

const areAllRequiredFiltersInPlace = (
  currentFilters: OrdersAssignedStoreFilters,
  currentView: AssignedOrdersViewTab,
) => {
  const requiredFilters: Array<keyof OrdersAssignedStoreFilters> = ['search'];
  if (currentView === AssignedOrdersViewTab.DRIVE) {
    requiredFilters.push('driverIds');
  } else if (currentView === AssignedOrdersViewTab.FOREMAN) {
    requiredFilters.push('foremen');
  } else if (currentView === AssignedOrdersViewTab.COLLABORATOR) {
    requiredFilters.push('collaborators');
  } else if (currentView === AssignedOrdersViewTab.SALES) {
    requiredFilters.push('salespeople');
  }

  const isSearching = Boolean(currentFilters.search?.length);

  if (isSearching) {
    return true;
  }

  const isReadyToFetchOrders = requiredFilters.every(
    (filter) => filter in currentFilters,
  );
  return isReadyToFetchOrders;
};

interface OrdersViewProps {
  viewSelector: ReactNode;
  view: AssignedOrdersViewTab;
}

const OrdersView = observer(({ viewSelector, view }: OrdersViewProps) => {
  const theme = useTheme();
  const { ordersAssignedStore, ordersDispatchStore, userStore, toasterStore } =
    useStores();
  const {
    acceptOrder,
    bulkAssignJobs,
    bulkCopyAssignments,
    bulkSendJobs,
    cancelOrder,
    completeOrder,
    createJobFromOrder,
    handleAssigneeDragCancel,
    handleAssigneeDragEnd,
    handleAssigneeDragStart,
    subscribeToBulkAssignJobsRTU,
    subscribeToBulkCopyAssignmentsRTU,
    subscribeToJobsRTU,
    subscribeToJobUnassignmentRTU,
    subscribeToOrdersRTU,
  } = useOrdersDispatch();

  const readOnly = view === AssignedOrdersViewTab.COLLABORATOR;

  const { getCompanyOrdersByCompanyId } = useOrdersAssigned();
  const {
    acceptOrder: acceptOrderEvent,
    holdOrder,
    requestOrder,
    startOrder,
    weatherHoldOrder,
  } = useOrders();

  const [isSendingSelectedJobs, setIsSendingSelectedJobs] = useState(false);
  const isSendingSelectedJobsRef = useRef(false);
  const [ordersFetchState, setOrdersFetchState] = useState({
    ...ordersFetchInitialState,
  });
  const [orderActionDialog, setOrderActionDialog] = useState<OrderActionDialogState>({
    isOpen: false,
  });

  const [orderFormState, setOrderFormState] = useState<OrderFormState>({
    drawer: { isMinimized: false, isOpen: false },
    confirmationDialog: { isOpen: false },
  });

  const bulkActions = useOrdersViewBulkActionsBar(ordersDispatchStore.orderJobsSnapshot);
  const isBulkActionsBarVisible = bulkActions.jobsCount > 0;
  const hasChildCompanies = userStore.userCompanies.length > 1;

  // An order's own materials belong to its own company, not necessarily the
  // acting user's, so bulk-editing material is only safe when every checked
  // job's order resolves to the same company — once that holds, any one of
  // those orders' ids is valid for the materials-scoped typeahead call.
  const materialsOrderId = useMemo(() => {
    const checkedOrderIds = [
      ...bulkActions.jobsWithEditableNotes.uniqueOrdersHavingCheckedJobs,
    ];
    const checkedOrders = checkedOrderIds.map((checkedOrderId) =>
      ordersDispatchStore.orders.find((order) => order.id === checkedOrderId),
    );

    const everyCheckedOrderSharesOneCompany =
      checkedOrders.every((order) => order?.company?.id) &&
      new Set(checkedOrders.map((order) => order?.company?.id)).size === 1;

    return everyCheckedOrderSharesOneCompany ? checkedOrderIds[0] : undefined;
  }, [bulkActions.jobsWithEditableNotes, ordersDispatchStore.orders]);

  const companyId = getEffectiveUserCompanyId(userStore);
  const currentUserId = userStore.user?.id;
  const isReadyToFetchOrders = areAllRequiredFiltersInPlace(
    ordersAssignedStore.filters,
    view,
  );
  const currentFilters = JSON.stringify(
    ordersAssignedStore.filters,
    // this is to presever end & start dates when "All Dates" filters is selected, as
    // we want to keep track of the whole set of filters applied
    (_, value) => value ?? null,
  );
  const previousFilters = usePrevious(currentFilters);

  const userPermissions = userStore.getPermissions();

  const openOrderActionDialog = useCallback(
    (dialog: OrderActionDialog, order?: Order) => {
      setOrderActionDialog((state) => ({ ...state, isOpen: true, dialog, order }));
    },
    [],
  );

  const closeOrderActionDialog = useCallback(() => {
    setOrderActionDialog((state) => ({ ...state, isOpen: false, order: undefined }));
    bulkActions.closeDialog('textAllDrivers');
  }, []);

  const dismissCurrentDialogAndOpenDrawerForOrder = useCallback((order: Order) => {
    setOrderFormState((state) => ({
      ...state,
      drawer: { isMinimized: false, isOpen: false },
    }));

    setTimeout(() => {
      setOrderFormState((state) => ({
        ...state,
        drawer: { isMinimized: false, isOpen: true, order },
      }));
    }, MODAL_DIALOG_ANIMATION_DURATION);
  }, []);

  const handleSendSelectedJobs = async () => {
    if (isSendingSelectedJobsRef.current) {
      return;
    }

    isSendingSelectedJobsRef.current = true;
    setIsSendingSelectedJobs(true);

    try {
      await bulkSendJobs(bulkActions.sendableJobs, companyId);
      bulkActions.clearAllCheckedOrdersAndJobs();
    } catch {
      // Error is handled by the hook
    } finally {
      isSendingSelectedJobsRef.current = false;
      setIsSendingSelectedJobs(false);
    }
  };

  const handleEditOrder = useCallback(
    (order: Order) => {
      if (orderFormState.drawer.isMinimized) {
        // drawer is for editing an order
        if (orderFormState.drawer.order?.id) {
          // same order -> open drawer right away
          if (orderFormState.drawer.order.id === order.id) {
            setOrderFormState((state) => ({
              ...state,
              drawer: {
                ...state.drawer,
                isMinimized: false,
              },
            }));
          }

          // different order -> ask for confirmation
          else {
            setOrderFormState((state) => ({
              ...state,
              confirmationDialog: { isOpen: true, order },
            }));
          }
        }

        // drawer is for creating an order -> ask for confirmation
        else {
          setOrderFormState((state) => ({
            ...state,
            confirmationDialog: { isOpen: true, order },
          }));
        }
      } else {
        setOrderFormState((state) => ({
          ...state,
          drawer: { isMinimized: false, isOpen: true, order },
        }));
      }
    },
    [orderFormState],
  );

  const handleAcceptOrder = useCallback(
    async (orderId: string) => {
      try {
        const acceptedOrder = await acceptOrder(orderId);

        if (acceptedOrder) {
          toasterStore.push(
            alert(
              t('dispatch.order.updated', { name: acceptedOrder.orderId }),
              AlertTypes.success,
            ),
          );
        }
      } catch {
        // Error is handled by the hook
      }
    },
    [acceptOrder, toasterStore],
  );

  const handleAllCardsExpandedStateChange = useCallback((expandedState: string) => {
    ordersAssignedStore.setOrderCardsExpandedState({
      allCards: expandedState,
      cardsByOrder: new Map(),
    });
  }, []);

  const handleBulkAssignJobsUpdateReceived = useCallback(
    (data: UserBulkAssignJobsChannel_Read) => {
      const hasSuccessfulUpdates = (data.assigned_job_friendly_ids?.length ?? 0) > 0;
      const hasFailedUpdates = (data.failed_job_friendly_ids?.length ?? 0) > 0;
      const failedJobIds = data.failed_job_friendly_ids
        ?.map((jobId) => `TRD-${jobId}`)
        .join(', ');

      if (hasSuccessfulUpdates && hasFailedUpdates) {
        toasterStore.push(
          alert(
            t('dispatch.order.bulk_jobs_sent', {
              success_num: data.assigned_job_friendly_ids?.length,
              failed_num: data.failed_job_friendly_ids?.length,
              failed_ids: `${failedJobIds}`,
            }),
            AlertTypes.error,
          ),
        );
      } else if (hasFailedUpdates) {
        toasterStore.push(
          alert(
            t('dispatch.order.bulk_jobs_sent_all_updates_failed', {
              failed_num: data.failed_job_friendly_ids?.length,
              failed_ids: `${failedJobIds}`,
            }),
            AlertTypes.error,
          ),
        );
      } else {
        toasterStore.push(
          alert(
            t('dispatch.order.bulk_jobs_sent_all_updates_successful', {
              success_num: data.assigned_job_friendly_ids?.length,
            }),
            AlertTypes.success,
          ),
        );
      }
    },
    [],
  );

  const handleBulkCopyAssignmentsUpdateReceived = useCallback(
    (data: UserBulkCopyAssignmentsChannel_Read) => {
      toasterStore.push(
        alert(
          t('dispatch.order.bulk_jobs_sent', {
            success_num: data.copied_job_friendly_ids?.length,
            failed_num: data.failed_job_friendly_ids?.length,
            failed_ids: ` ${data.failed_job_friendly_ids?.map((v) => `TRD-${v}`).join(', ')}`,
          }),
          (data.failed_job_friendly_ids?.length ?? 0) > 0
            ? AlertTypes.error
            : AlertTypes.success,
        ),
      );
    },
    [],
  );

  const handleCancelOrder = useCallback(
    async (orderId: string) => {
      try {
        const cancelledOrder = await cancelOrder(orderId);

        if (cancelledOrder) {
          if (orderFormState.drawer.order?.id === orderId) {
            setOrderFormState((state) => ({
              ...state,
              drawer: { isMinimized: false, isOpen: false },
            }));
          }

          toasterStore.push(
            alert(
              t('dispatch.order.canceled', { name: cancelledOrder.orderId }),
              AlertTypes.success,
            ),
          );
        }
      } catch {
        // Error is handled by the hook
      } finally {
        closeOrderActionDialog();
      }
    },
    [cancelOrder, closeOrderActionDialog, orderFormState.drawer.order?.id],
  );

  const handleOrderCloned = useCallback(
    (order: Order) => {
      toasterStore.push(
        alert(t('dispatch.order.copied', { name: order.orderId }), AlertTypes.success),
      );

      closeOrderActionDialog();
    },
    [closeOrderActionDialog],
  );

  const handleCompleteOrder = useCallback(
    async (orderId: string) => {
      try {
        const completedOrder = await completeOrder(orderId);

        if (completedOrder) {
          toasterStore.push(
            alert(
              t('dispatch.order.completed', { name: completedOrder.orderId }),
              AlertTypes.success,
            ),
          );
        }
      } catch {
        // Error is handled by the hook
      }
    },
    [completeOrder, toasterStore],
  );

  const handleCreateJobFromOrder = useCallback(
    async (orderId: string) => {
      try {
        const createdJob = await createJobFromOrder(orderId);

        toasterStore.push(
          alert(
            t('dispatch.order.added_job', { name: createdJob.jobId }),
            AlertTypes.success,
          ),
        );
      } catch {
        // Error is handled by the hook
      }
    },
    [createJobFromOrder, toasterStore],
  );

  const handleRefreshOrdersClick = useCallback(() => {
    setOrdersFetchState((state) => ({
      ...state,
      shouldFetch: true,
      reason: FetchReason.REFRESH,
    }));
  }, []);

  const handleLoadMoreOrders = useCallback(() => {
    if (ordersAssignedStore.hasMore && !ordersAssignedStore.isLoadingOrders) {
      setOrdersFetchState((state) => ({
        ...state,
        shouldFetch: true,
        reason: FetchReason.INFINITE_SCROLL,
      }));
    }
  }, [ordersAssignedStore.hasMore, ordersAssignedStore.isLoadingOrders]);

  const renderOrderCard = (order: Order) => (
    <OrderDispatchCard
      key={order.id}
      order={order}
      readOnly={readOnly}
      companyHasChildCompanies={hasChildCompanies}
      expandedState={
        ordersAssignedStore.orderCardsExpandedState.cardsByOrder.get(order.id) ??
        ordersAssignedStore.orderCardsExpandedState.allCards
      }
      checkedJobs={bulkActions.checkedOrdersAndJobs[order.id]}
      onBulkAssignMultipleClick={() => {
        openOrderActionDialog(OrderActionDialog.BULK_ASSIGN_MULTIPLE, order);
      }}
      onAcceptOrder={() => handleAcceptOrder(order.id)}
      onCheckedStateChange={bulkActions.updateCheckedOrdersAndJobs}
      onCloneOrderClick={() => {
        const updatedOrderInList = ordersAssignedStore.orders.find(
          (storeOrder) => storeOrder.id === order.id,
        );

        if (updatedOrderInList) {
          openOrderActionDialog(OrderActionDialog.CLONE_ORDER, updatedOrderInList);
        }
      }}
      onCopyAndEditClick={() => {
        console.warn('Copy and Edit not implemented for Assigned Orders view');
      }}
      onCreateJobFromOrderClick={() => handleCreateJobFromOrder(order.id)}
      onEditClick={() => handleEditOrder(order)}
      onExpandedStateChange={ordersAssignedStore.toggleOrderCardExpandedState}
      onOrderStateChange={async (nextOrderState) => {
        switch (nextOrderState) {
          case OrderState.CANCELED:
            openOrderActionDialog(OrderActionDialog.CANCEL_ORDER, order);
            break;
          case OrderState.COMPLETED:
            void handleCompleteOrder(order.id);
            break;
          case OrderState.REJECTED:
            openOrderActionDialog(OrderActionDialog.REJECT_ORDER, order);
            break;
          case OrderState.ACCEPTED:
            await acceptOrderEvent(order.id);
            break;
          case OrderState.IN_PROGRESS:
            await startOrder(order.id);
            break;
          case OrderState.PENDING_REQUEST:
            await requestOrder(order.id);
            break;
          case OrderState.HOLD:
            await holdOrder(order.id);
            break;
          case OrderState.WEATHER_HOLD:
            await weatherHoldOrder(order.id);
            break;
        }
      }}
      onRejectOrder={() => {
        openOrderActionDialog(OrderActionDialog.REJECT_ORDER, order);
      }}
      onTextAllDriversClick={() => {
        openOrderActionDialog(OrderActionDialog.TEXT_ALL_DRIVERS, order);
      }}
    />
  );

  const fetchOrders = () => {
    if (ordersFetchState.reason === FetchReason.FILTERS_CHANGED) {
      handleAllCardsExpandedStateChange(ORDER_ASSIGNED_CARD_DEFAULT_EXPANDED_STATE);
    }

    if (ordersAssignedStore.deepLinkedOrder) {
      return;
    }

    getCompanyOrdersByCompanyId({ companyId }).then(() => {
      setOrdersFetchState((state) => ({
        ...state,
        shouldFetch: false,
        initialFetchComplete: true,
      }));
    });
  };

  useEffect(() => {
    let ordersRTUSubscription: ReturnType<typeof subscribeToOrdersRTU> | null = null;
    let jobsRTUSubscription: ReturnType<typeof subscribeToJobsRTU> | null = null;
    let bulkAssignJobsRTUSubscription: ReturnType<
      typeof subscribeToBulkAssignJobsRTU
    > | null = null;
    let bulkCopyAssignmentsRTUSubscription: ReturnType<
      typeof subscribeToBulkCopyAssignmentsRTU
    > | null = null;
    let jobUnassignmentRTUSubscription: ReturnType<
      typeof subscribeToJobUnassignmentRTU
    > | null = null;

    if (companyId) {
      ordersRTUSubscription = subscribeToOrdersRTU(companyId);
      jobsRTUSubscription = subscribeToJobsRTU(companyId);
      bulkAssignJobsRTUSubscription = subscribeToBulkAssignJobsRTU(
        companyId,
        handleBulkAssignJobsUpdateReceived,
      );
      bulkCopyAssignmentsRTUSubscription = subscribeToBulkCopyAssignmentsRTU(
        companyId,
        handleBulkCopyAssignmentsUpdateReceived,
      );
      jobUnassignmentRTUSubscription = subscribeToJobUnassignmentRTU(companyId);
    }

    return () => {
      ordersRTUSubscription?.unsubscribe?.();
      jobsRTUSubscription?.unsubscribe?.();
      bulkAssignJobsRTUSubscription?.unsubscribe?.();
      bulkCopyAssignmentsRTUSubscription?.unsubscribe?.();
      jobUnassignmentRTUSubscription?.unsubscribe?.();
    };
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      setOrdersFetchState({ ...ordersFetchInitialState, shouldFetch: true });
    }
  }, [companyId]);

  useEffect(() => {
    if (ordersFetchState.shouldFetch) {
      if (
        [FetchReason.FILTERS_CHANGED, FetchReason.REFRESH].includes(
          ordersFetchState.reason,
        )
      ) {
        bulkActions.clearAllCheckedOrdersAndJobs();
      }

      fetchOrders();
    }
  }, [ordersFetchState.shouldFetch, ordersFetchState.reason]);

  useEffect(() => {
    if (!isReadyToFetchOrders) {
      return;
    }

    setOrdersFetchState((state) => {
      if (!state.initialFetchComplete) {
        return { ...state, shouldFetch: true, reason: FetchReason.INITIAL_FETCH };
      }

      if (currentFilters !== previousFilters) {
        return { ...state, shouldFetch: true, reason: FetchReason.FILTERS_CHANGED };
      }

      return { ...state, shouldFetch: false };
    });
  }, [isReadyToFetchOrders, currentFilters, previousFilters]);

  useEffect(() => {
    if (view === AssignedOrdersViewTab.DRIVE) {
      ordersAssignedStore.setFilters(
        {
          search: '',
          driverIds: [currentUserId],
          foremen: undefined,
          collaborators: undefined,
          salespeople: undefined,
        },
        true,
      );
    } else if (view === AssignedOrdersViewTab.FOREMAN) {
      ordersAssignedStore.setFilters(
        {
          search: '',
          foremen: [currentUserId],
          driverIds: undefined,
          collaborators: undefined,
          salespeople: undefined,
        },
        true,
      );
    } else if (view === AssignedOrdersViewTab.COLLABORATOR) {
      ordersAssignedStore.setFilters(
        {
          search: '',
          collaborators: [currentUserId],
          driverIds: undefined,
          foremen: undefined,
          salespeople: undefined,
        },
        true,
      );
    } else if (view === AssignedOrdersViewTab.SALES) {
      ordersAssignedStore.setFilters(
        {
          search: '',
          collaborators: undefined,
          driverIds: undefined,
          foremen: undefined,
          salespeople: [currentUserId],
        },
        true,
      );
    }

    return () => {
      ordersAssignedStore.setDeepLinkedOrder();
      ordersAssignedStore.setFilters(
        {
          search: '',
          driverIds: undefined,
          foremen: undefined,
          collaborators: undefined,
          salespeople: undefined,
        },
        true,
      );
    };
  }, [currentUserId, view]);

  const isForemanView = view === AssignedOrdersViewTab.FOREMAN;
  const isSalesView = view === AssignedOrdersViewTab.SALES;

  const isProjectsTabSelected =
    ordersAssignedStore.targetEntity === OrdersAssignedTargetEntity.PROJECTS;
  const shouldShowProjectsList = isSalesView && isProjectsTabSelected;

  return (
    <>
      <DndContext
        onDragCancel={handleAssigneeDragCancel}
        onDragEnd={handleAssigneeDragEnd}
        onDragStart={handleAssigneeDragStart}
      >
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: theme.palette.background.paper,
            borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
            display: 'flex',
            justifyContent: 'space-between',
            px: 2,
          }}
        >
          {viewSelector}

          {shouldShowProjectsList ? null : (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                ml: 'auto',
              }}
            >
              <ExpandedStatesMenu
                expandedState={ordersAssignedStore.orderCardsExpandedState.allCards}
                onExpandedStateChange={handleAllCardsExpandedStateChange}
              />
              {(userPermissions.canCreateOrder ||
                userPermissions.canCreateOrderRequest) && (
                <CreateOrderButton
                  isForResumeOrderCreation={orderFormState.drawer.isMinimized}
                  disabled={userStore.isViewOnlyUser || readOnly}
                  onClick={() => {
                    if (orderFormState.drawer.isMinimized) {
                      setOrderFormState((state) => ({
                        ...state,
                        drawer: {
                          ...state.drawer,
                          isMinimized: false,
                        },
                      }));
                    } else {
                      setOrderFormState((state) => ({
                        ...state,
                        drawer: { isMinimized: false, isOpen: true },
                      }));
                    }
                  }}
                />
              )}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            alignItems: 'center',
            bgcolor: theme.palette.background.paper,
            borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
            display: shouldShowProjectsList ? 'none' : 'flex',
            minHeight: `${ORDER_DISPATCH_V3_GRID_ROW_HEIGHT_IN_PX}px`,
            px: 2,
          }}
        >
          <OrdersAssignedDatesFilter dateRangePickerMode="icon" dateRangeUseTodayFormat />

          <RefreshOrdersListButton
            disabled={ordersAssignedStore.isLoadingOrders}
            onClick={handleRefreshOrdersClick}
            sx={{ ml: 'auto' }}
          />
        </Box>

        {(isForemanView || isSalesView) && (
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: theme.palette.background.paper,
              borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              display: 'flex',
              gap: 1,
              px: 2,
              py: 1,
            }}
          >
            {isForemanView && (
              <OrdersStatusFilter
                storageTabKey={OrdersAssignedStorageKeys.TAB_PARAM}
                storeName="ordersAssignedStore"
              />
            )}

            {isSalesView && <SalespersonViewSelector />}
          </Box>
        )}

        <Box>
          <Box
            sx={{
              height: `calc(100vh - ${HEADER_HEIGHT}px - ${ORDER_DISPATCH_V3_GRID_ROW_HEIGHT_IN_PX}px * ${isForemanView || isSalesView ? 2 : 1} - 2px - var(--tread-agent-dock-height, 0px))`,
              overflow: 'hidden',
            }}
          >
            {shouldShowProjectsList ? (
              <AssignedOrdersProjectsQueryContextProvider companyId={companyId}>
                <ProjectsList />
              </AssignedOrdersProjectsQueryContextProvider>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  pt: 0.5,
                }}
              >
                <AssignedOrdersList
                  hasMore={ordersAssignedStore.hasMore}
                  isLoading={ordersAssignedStore.isLoadingOrders}
                  onLoadMore={handleLoadMoreOrders}
                  orders={ordersAssignedStore.orders}
                  renderOrderCard={renderOrderCard}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DndContext>
      {isBulkActionsBarVisible && (
        <OrdersViewBulkActionsBar
          jobsCount={bulkActions.jobsCount}
          onClearAll={bulkActions.clearAllCheckedOrdersAndJobs}
        >
          <BulkActionsBarButton
            color="inherit"
            disabled={bulkActions.copyableJobs.length === 0}
            onClick={() => bulkActions.openDialog('copyVendorAssignments')}
            startIcon={<ContentCopyOutlined />}
          >
            {t('actions.copy_to')}
          </BulkActionsBarButton>

          <BulkActionsBarButton
            color="inherit"
            onClick={() => bulkActions.openDialog('editJobNotes')}
            startIcon={<EditOutlined />}
          >
            {t('dispatch.order.edit')}
          </BulkActionsBarButton>

          <BulkActionsBarButton
            color="inherit"
            disabled={bulkActions.jobsWithTextableDrivers.length === 0}
            onClick={() => bulkActions.openDialog('textAllDrivers')}
            startIcon={<SmsOutlined />}
          >
            {t('actions.text')}
          </BulkActionsBarButton>

          <Tooltip title={t('dispatch.order.assign_to_vendor_tooltip')}>
            <Box sx={{ display: 'inline-block' }}>
              <BulkActionsBarButton
                color="inherit"
                disabled={bulkActions.assignableJobs.length === 0}
                onClick={() => bulkActions.openDialog('assign')}
                startIcon={<PersonAddOutlined />}
              >
                {t('dispatch.order.assign_to_vendor')}
              </BulkActionsBarButton>
            </Box>
          </Tooltip>

          <BulkActionsBarButton
            color="brandV2Green"
            disabled={bulkActions.sendableJobs.length === 0 || isSendingSelectedJobs}
            onClick={handleSendSelectedJobs}
            startIcon={<SendOutlined />}
          >
            {t('actions.send')}
          </BulkActionsBarButton>
        </OrdersViewBulkActionsBar>
      )}
      <OrderForm
        defaultDate={ordersAssignedStore.filters.startDate}
        isMinimized={orderFormState.drawer.isMinimized}
        isOpen={orderFormState.drawer.isOpen}
        onClose={() => {
          setOrderFormState(() => ({
            drawer: { isMinimized: false, isOpen: false },
            confirmationDialog: { isOpen: false },
          }));
        }}
        onMinimize={() => {
          setOrderFormState((state) => ({
            ...state,
            drawer: {
              ...state.drawer,
              isMinimized: true,
            },
          }));
        }}
        order={orderFormState.drawer.order}
      />
      <Dialog
        isOpen={orderFormState.confirmationDialog.isOpen}
        maxWidth="sm"
        onClose={() => {
          setOrderFormState((state) => ({
            ...state,
            confirmationDialog: { ...state.confirmationDialog, isOpen: false },
          }));
        }}
        preventCloseOnEscapeKeyDown
      >
        <DialogHeader
          onClose={() => {
            setOrderFormState((state) => ({
              ...state,
              confirmationDialog: { ...state.confirmationDialog, isOpen: false },
            }));
          }}
        >
          {t('dispatch.order.edit_order')}
        </DialogHeader>

        <DialogContent>
          <Typography>
            {t('dispatch.dispatch_v2.edit_confirmation_dialog_text', {
              action: orderFormState.drawer.order?.id
                ? t('common.edited').toLowerCase()
                : t('common.created').toLowerCase(),
              orderId: orderFormState.confirmationDialog.order?.orderId,
            })}
          </Typography>
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            onClick={() => {
              setOrderFormState((state) => ({
                ...state,
                confirmationDialog: { ...state.confirmationDialog, isOpen: false },
              }));
            }}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallButton
            color="primary"
            onClick={() => {
              const orderToOpen = orderFormState.confirmationDialog.order;

              setOrderFormState((state) => ({
                ...state,
                confirmationDialog: { ...state.confirmationDialog, isOpen: false },
              }));

              if (orderToOpen) {
                dismissCurrentDialogAndOpenDrawerForOrder(orderToOpen);
              }
            }}
            variant="contained"
          >
            {t('actions.confirm')}
          </SmallButton>
        </DialogActions>
      </Dialog>
      <BulkAssignJobsDialog
        accountId={bulkActions.dialogsState.assign.selectedVendorAccountId ?? null}
        companyId={companyId}
        filterDate={ordersAssignedStore.filters.startDate}
        isOpen={bulkActions.dialogsState.assign.isOpen}
        onClose={() => bulkActions.closeDialog('assign')}
        onConfirm={() => {
          const { assignableJobs, dialogsState } = bulkActions;
          const { selectedVendorAccountId } = dialogsState.assign;

          if (selectedVendorAccountId && assignableJobs.length) {
            void bulkAssignJobs(assignableJobs, selectedVendorAccountId, companyId).catch(
              () => {
                // Error is handled by the hook
              },
            );
          }

          bulkActions.closeDialog('assign');
        }}
        onSelectedAccountChange={bulkActions.updateBulkAssignJobsSelectedAccount}
      />
      <BulkAssignMultipleJobsDialog
        filterDate={ordersAssignedStore.filters.startDate}
        isOpen={
          orderActionDialog.isOpen &&
          orderActionDialog.dialog === OrderActionDialog.BULK_ASSIGN_MULTIPLE
        }
        onClose={closeOrderActionDialog}
        onConfirm={closeOrderActionDialog}
        order={orderActionDialog.order}
      />
      <BulkEditJobDialog
        isOpen={bulkActions.dialogsState.editJobNotes.isOpen}
        jobIds={bulkActions.jobsWithEditableNotes.jobIds}
        onClose={() => bulkActions.closeDialog('editJobNotes')}
        orderId={materialsOrderId}
        defaultValues={{
          notes: bulkActions.jobsWithEditableNotes.defaultNotes ?? undefined,
        }}
      />
      <CopyVendorAssignmentsDialog
        companyId={companyId}
        isOpen={bulkActions.dialogsState.copyVendorAssignments.isOpen}
        orderId={bulkActions.dialogsState.copyVendorAssignments.selectedOrderId}
        onClose={() => bulkActions.closeDialog('copyVendorAssignments')}
        onConfirm={() => {
          const { copyableJobs, dialogsState } = bulkActions;
          const { selectedOrderId } = dialogsState.copyVendorAssignments;

          if (selectedOrderId && copyableJobs.length) {
            void bulkCopyAssignments(copyableJobs, selectedOrderId, companyId).catch(
              () => {
                // Error is handled by the hook
              },
            );
          }

          bulkActions.closeDialog('copyVendorAssignments');
        }}
        onSelectedOrderChange={bulkActions.updateBulkCopyAssignmentsSelectedOrder}
      />
      {orderActionDialog.order && (
        <CloneOrderDialog
          isOpen={
            orderActionDialog.isOpen &&
            orderActionDialog.dialog === OrderActionDialog.CLONE_ORDER
          }
          onClose={closeOrderActionDialog}
          onOrderCloned={handleOrderCloned}
          order={orderActionDialog.order}
        />
      )}
      <SendTextDialog
        isOpen={
          bulkActions.dialogsState.textAllDrivers.isOpen ||
          (orderActionDialog.isOpen &&
            orderActionDialog.dialog === OrderActionDialog.TEXT_ALL_DRIVERS)
        }
        onClose={closeOrderActionDialog}
        mode={
          bulkActions.dialogsState.textAllDrivers.isOpen
            ? 'bulk_drivers'
            : 'order_drivers'
        }
        source={
          bulkActions.dialogsState.textAllDrivers.isOpen
            ? bulkActions.jobsWithTextableDrivers
            : orderActionDialog.order
        }
      />
      <ConfirmationDialog
        isBusy={ordersAssignedStore.isCancellingOrder}
        isOpen={
          orderActionDialog.isOpen &&
          orderActionDialog.dialog === OrderActionDialog.CANCEL_ORDER
        }
        onClose={closeOrderActionDialog}
        onConfirm={() => {
          if (orderActionDialog.order?.id) {
            handleCancelOrder(orderActionDialog.order.id);
          }
        }}
        title={t('dispatch.order.cancel_order')}
      >
        <Typography>{t('dispatch.order.cancel_description')}</Typography>
      </ConfirmationDialog>
      <RejectOrderDialog
        isOpen={
          orderActionDialog.isOpen &&
          orderActionDialog.dialog === OrderActionDialog.REJECT_ORDER
        }
        onClose={closeOrderActionDialog}
        order={orderActionDialog.order}
      />
    </>
  );
});

interface AssignedOrdersListProps {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  orders: Order[];
  renderOrderCard: (order: Order) => ReactNode;
}

function AssignedOrdersList({
  hasMore,
  isLoading,
  onLoadMore,
  orders,
  renderOrderCard,
}: AssignedOrdersListProps) {
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const itemCount = orders.length + 1;

  const handleEndReached = useCallback(() => {
    if (hasMore && !isLoading) {
      onLoadMore();
    }
  }, [hasMore, isLoading, onLoadMore]);

  const renderOrderItem = (index: number) => {
    const order = orders[index];

    if (index >= orders.length) {
      if (isLoading) {
        return <OrdersLoadingIndicator />;
      }

      if (!hasMore) {
        return (
          <Box
            sx={{
              px: ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE,
              py: 1,
            }}
          >
            <AllOrdersLoadedIndicator hasOrders={orders.length > 0} />
          </Box>
        );
      }

      return null;
    }

    return (
      <Box sx={{ px: ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE, py: 1 }}>
        {renderOrderCard(order)}
      </Box>
    );
  };

  return (
    <Virtuoso
      ref={virtuosoRef}
      data={Array.from({ length: itemCount }, (_, index) => index)}
      endReached={handleEndReached}
      itemContent={renderOrderItem}
      overscan={5}
      style={{ height: '100%' }}
    />
  );
}

function OrdersLoadingIndicator() {
  return (
    <Box
      sx={(theme) => ({
        alignItems: 'center',
        display: 'flex',
        height: `calc(30px + ${theme.spacing(2)})`,
        justifyContent: 'center',
        px: ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE,
      })}
    >
      <LoadingSpinner isVisible />
    </Box>
  );
}

function AllOrdersLoadedIndicator({ hasOrders }: { hasOrders: boolean }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',

        '&::before': {
          backgroundColor: theme.brandV2.colors.treadGray6,
          content: "''",
          display: 'block',
          height: '1px',
          left: 0,
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
        },
      }}
    >
      <Typography
        color={theme.brandV2.colors.treadGray6}
        sx={{
          bgcolor: theme.brandV2.colors.treadGray8,
          fontSize: '12px',
          fontWeight: '700',
          position: 'relative',
          px: 1,
          textAlign: 'center',
          textTransform: 'uppercase',
        }}
      >
        {hasOrders ? t('dispatch.dispatch_v2.all_orders_loaded') : t('common.no_data')}
      </Typography>
    </Box>
  );
}

export default OrdersView;
